import React from 'react';
import { Box, CssBaseline, ThemeProvider, Typography } from '@mui/material';
import ConstructionPriceEstimator from '../stages/ConstructionPriceEstimator';
import { FlowContext } from '../components/StageWrapper';
import { createAppTheme } from '../theme';
import { approximateCostPlanFixture } from './approximateCostPlanFixture';
import type { ApproximateCostPlanPublic } from '../api/approximateCostPlan';
import type { SiteStaffingRoleSelection } from '../api/approximateCostPlan';
import { emptyApproximateSettings, normalizeApproximateSettings, type ApproximateCostPlanSettings } from '../api/approximateCostPlan';

const flowId = 'construction-cost-snapshot-e2e';
const jobStorageKey = 'construction-cost-snapshot-e2e-job';
const romSettingsKey = 'construction-cost-snapshot-e2e-rom-settings';
const laborPlanStatusKey = 'construction-cost-snapshot-e2e-labor-plan-status';
const calculationCountKey = 'construction-cost-snapshot-e2e-calculation-count';

/** The normal fixture stays self-contained; the HTTP harness is opt-in only. */
export const shouldInstallConstructionCostSnapshotFetchShim = (
  httpHarness = process.env.REACT_APP_E2E_FIXTURE_HTTP,
) => httpHarness !== '1';

const initialDraft = {
  draft_revision: 4,
  content_hash: 'fixture-accepted-draft-hash',
  project_location: { country: 'US', state: 'TX', county: 'Harris', city: 'Houston', postal_code: '77002' },
  estimate_date: '2026-09-03', construction_type: 'Heavy civil pavement repair', davis_bacon_construction_type: 'Highway',
  size_drivers: [], work_items: [], project_assumptions: [], exclusions: [], adjustments: [], required_acknowledgements: [],
};

let draft = { ...initialDraft };
export const defaultApproximateRomSettings = (): ApproximateCostPlanSettings => ({
  ...emptyApproximateSettings(),
  recipe_key: 'drywall_level4_wood_v1',
  enabled: true,
  work_item_description: 'Drywall',
  net_quantity: '1000',
  quantity_unit: 'square_foot',
});
const laborPlanRomSettings = (): ApproximateCostPlanSettings => ({
  ...defaultApproximateRomSettings(),
  material_components: [{
    occurrence_id: 'fixture-drywall', component_key: 'drywall_level4_wood_v1',
    specification_id: 'single_layer_half_in_gypsum_board_level_4',
    quantity: '1000', unit: 'square_foot', confirmed: true,
  }],
});
const savedRomSettings = (): ApproximateCostPlanSettings => {
  try { return normalizeApproximateSettings(JSON.parse(window.localStorage.getItem(romSettingsKey) ?? 'null')) ?? defaultApproximateRomSettings(); }
  catch { return defaultApproximateRomSettings(); }
};

/** Starts the mixed takeoff visual check without preselecting a component. */
const savedMixedTakeoffSettings = (): ApproximateCostPlanSettings => {
  try { return normalizeApproximateSettings(JSON.parse(window.localStorage.getItem(romSettingsKey) ?? 'null')) ?? emptyApproximateSettings(); }
  catch { return emptyApproximateSettings(); }
};

/** Deliberate human-reviewed inputs in the local paint visual fixture only. */
const paintRomSettings = (acknowledged: boolean): ApproximateCostPlanSettings => ({
  ...emptyApproximateSettings(),
  recipe_key: 'interior_paint_flat_latex_v1',
  enabled: true,
  work_item_description: 'Interior painting',
  net_quantity: '1000',
  quantity_unit: 'square_foot',
  paint_coat_count: '2',
  ...(acknowledged ? {
    paint_coverage_sf_per_gallon_per_coat: '350',
    paint_waste_fraction: '0.10',
    paint_coverage_basis: 'planning_assumption' as const,
    paint_coverage_assumption_acknowledged: true,
  } : {}),
});

/**
 * Synthetic local-only primer settings. These use the public-source conditions
 * documented by the real integration, but deliberately do not call or claim
 * to call either source from this browser fixture.
 */
const primerRomSettings = (): ApproximateCostPlanSettings => ({
  ...emptyApproximateSettings(),
  enabled: true,
  material_components: [{
    occurrence_id: 'fixture-primer-wall-a',
    component_key: 'ppg_speedhide_6_2_interior_primer',
    specification_id: 'ppg_6_2_speedhide_interior_latex_quick_dry_sealer',
    quantity: '1000', unit: 'square_foot', confirmed: true,
    primer_coat_count: '1', primer_coverage_sf_per_gallon_per_coat: '400',
    primer_waste_fraction: '0', primer_coverage_basis: 'manufacturer_nominal',
    primer_coverage_evidence_acknowledged: true,
    primer_product_compatibility_confirmed: true,
    primer_smooth_nonporous_confirmed: true,
  }],
});

const savedPrimerRomSettings = (): ApproximateCostPlanSettings => {
  try {
    const saved = normalizeApproximateSettings(JSON.parse(window.localStorage.getItem(romSettingsKey) ?? 'null'));
    return saved?.material_components.some((item) => item.component_key === 'ppg_speedhide_6_2_interior_primer')
      ? saved : primerRomSettings();
  } catch { return primerRomSettings(); }
};

const scenarioName = () => new URLSearchParams(window.location.search).get('scenario') ?? 'eligible';

/** Isolated labor-plan review fixture; it never calls an external service. */
const fixtureLaborPreview = {
  craft_hours: [{ occurrence_id: 'paint-fixture', craft: 'Painter', quantity: '1000', unit: 'square_foot', hours_per_unit: '0.023', person_hours: '23', source_version: 'paint-v1', wage_state: 'pending_lookup', coverage_state: 'partial' }],
  unmodeled_work: ['Electrical work'], duplicate_occurrences: [], coverage_state: 'partial',
};
const fixtureLaborProposal = {
  proposal_id: 'labor-plan-0123456789abcdef0123456789abcdef', schema_version: 'labor-plan-proposal-v1', status: 'proposed', input_hash: 'fixture-input',
  expected_draft_revision: initialDraft.draft_revision, expected_draft_content_hash: initialDraft.content_hash,
  craft_hour_preview: fixtureLaborPreview,
  schedule: { average_concurrent_crew: '2', shift_hours: '8', parallel_work: false, derived_site_days: '2', confidence: 'low', rationale: 'The measured painting package does not describe the full contract.', coverage_state: 'partial', scope_label: 'Painting only' },
  site_staffing_roles: [
    { role_key: 'site-superintendent-fixture', role_kind: 'site_superintendent', soc_code: '11-9021', duty_description: '', mapping_reason: '', evidence_kind: 'owner_cited_sow', evidence_reference: 'Fixture SOW', evidence_quote: 'Provide a superintendent.', separate_staffing_required: true, coverage_provider: 'separately_staffed', person_key: 'person-superintendent', headcount: '1', shift_hours: '8', active_workdays: '2' },
    { role_key: 'site-safety-fixture', role_kind: 'site_safety', soc_code: '19-5011', duty_description: '', mapping_reason: '', evidence_kind: 'owner_cited_sow', evidence_reference: 'Fixture SOW', evidence_quote: 'Provide a site safety specialist.', separate_staffing_required: true, coverage_provider: 'separately_staffed', person_key: 'person-safety', headcount: '1', shift_hours: '8', active_workdays: '2' },
  ],
  field_basis: { 'site-superintendent-fixture': { headcount: 'contract_cited', shift_hours: 'ai_planning_assumption', active_workdays: 'ai_planning_assumption' } },
};
let laborFixtureJobPolls = 0;

/** Component-focused scenarios start with an already reviewed synthetic plan. */
const withAppliedLaborPlan = (
  settings: ApproximateCostPlanSettings,
): ApproximateCostPlanSettings => window.localStorage.getItem(romSettingsKey) !== null
  ? settings
  : {
    ...settings,
    labor_plan_provenance: fixtureLaborProposal.proposal_id,
    site_staffing_roles: fixtureLaborProposal.site_staffing_roles as SiteStaffingRoleSelection[],
  };

/** Isolate scenario tests while retaining state across a fixture remount. */
export const resetConstructionCostSnapshotFixtureState = () => {
  draft = { ...initialDraft };
  laborFixtureJobPolls = 0;
};

const savedJob = (): 'running' | 'completed' | 'cancelled' | null => {
  const value = window.localStorage.getItem(jobStorageKey);
  return value === 'running' || value === 'completed' || value === 'cancelled' ? value : null;
};

const storeJob = (status: 'running' | 'completed' | 'cancelled') => window.localStorage.setItem(jobStorageKey, status);

const response = (data: unknown, status = 200) => new Response(JSON.stringify({ success: true, data }), {
  status, headers: { 'Content-Type': 'application/json' },
});

const fixtureRevision = {
  revision: 8, status: 'calculated', mode: 'itemized', currency: 'USD', estimate_date: '2026-09-03',
  totals: {
    direct: { low: '950', base: '1000', high: '1050' }, allowances: { low: '0', base: '0', high: '0' }, adjustments: { low: '0', base: '0', high: '0' },
    low: '950', base: '1000', high: '1050', total: { low: '950', base: '1000', high: '1050' },
  },
  confidence: { band: 'moderate', reasons: [] }, assumptions: [], exclusions: [], warnings: [], adjustments: [], comparables: { items: [] },
  cost_data_summary: {
    decision: 'direct_cost_estimate', status: 'eligible', draft_revision: 4, covered_item_count: 1, included_item_count: 1,
    unresolved_items: [], source_freshness: 'current', provenance: [{ source_label: 'TxDOT approved bid-tab data', freshness_status: 'current' }],
  },
  work_items: [{
    id: 'tx-heavy-civil-item', description: 'Hot-mix asphalt pavement', quantity: '10', unit: 'ton',
    pricing_basis: 'installed_work', unit_cost: { low: '95', base: '100', high: '105' },
    installed_work: { low: '950', base: '1000', high: '1050' }, total: { low: '950', base: '1000', high: '1050' },
    component_inclusion: { labor: 'included_in_installed_work', materials: 'included_in_installed_work', equipment: 'included_in_installed_work', subcontract: 'included_in_installed_work' },
    provenance_summary: {
      source_label: 'TxDOT approved bid-tab data', effective_date: '2026-06-15', geography_fit: 'county', freshness_status: 'current',
      selected_observation_count: 7, observation_start_date: '2025-10-01', observation_end_date: '2026-06-15', snapshot_published_at: '2026-08-20T14:30:00Z',
      time_adjustment: { source_label: 'FHWA NHCCI', status: 'adjusted', observation_period: '2026Q2', reference_period: '2026Q3' },
    },
  }],
};

/** One synthetic safe projection of a unified result; no source is fetched. */
const fixtureUnifiedResearch = {
  schema_version: 'unified-source-research-v1',
  recommendation: {
    outcome: 'recommended', display_label: 'Recommended planning price',
    primary_source_label: 'Local public-owner bid evidence',
    display_shape: 'distribution', cohort_count: 3,
    range: { low: '91', median: '100', high: '112', unit_label: 'per installed unit', basis_label: 'Installed work', pricing_date_label: 'September 2026' },
    range_quality_warning: 'Wide price range—review before relying on it.',
    basis_label: 'Installed work', applicability_label: 'Harris County, Texas',
    evidence_summary: { primary_summary: 'Local public-owner bid evidence is the primary planning evidence.', context_summary: 'Previous federal awards were checked for context.' },
    limitation: 'Planning-grade evidence; not a vendor quote or complete estimate.',
    private_snapshot_id: 'never-render-this', candidate_score: 99,
  },
  evidence_drawer: { limitation: 'Planning-grade evidence; not a vendor quote or complete estimate.', private_policy: 'never-render-this' },
  btb_breakdown: {
    status: 'partial', heading: 'Technology cost details',
    source_label: 'DOE/NREL Buildings Technology Baseline (2024)', geography_label: 'National', value_year_label: '2023 dollars',
    unmatched_count: 1,
    items: [{
      project_item_label: 'Replace rooftop heat-pump unit', technology_label: 'Commercial air-source heat pump',
      end_use_label: 'HVAC', building_segment: 'Commercial', fuel_label: 'Electricity', model_year: 2023,
      scenario_label: 'Reference', construction_scope_label: 'Retrofit', value_basis_label: 'Project-scaled',
      retail_benchmark: { low: '1000', median: '1200', high: '1500', unit_label: 'per unit' },
      installation_allowance: { low: '100', median: '250', high: '400', unit_label: 'per unit' },
      installed_total: { low: '1100', median: '1450', high: '1900', unit_label: 'per unit' },
      range_quality_warning: 'Wide price range—review before relying on it.',
      source_quality_summary: 'Published national technology model.',
      attribution: 'DOE/NREL Buildings Technology Baseline (2024), CC BY 4.0.',
      limitation: 'Planning baseline for a supported building-energy technology; not a project-specific quote or complete construction estimate.',
      concept_key: 'never-render-this', snapshot_hash: 'never-render-this',
    }],
    private_manifest_id: 'never-render-this', source_url: 'https://example.invalid/private',
  },
  lanes: [
    { label: 'Previous federal awards', state: 'complete', message: 'Comparable awards checked.' },
    { label: 'National planning baseline', state: 'complete', message: 'Planning baseline selected.' },
  ],
};

const fixtureGeorgiaObservedEvidence = {
  status: 'available', heading: 'Georgia public-owner price evidence',
  scope: 'Georgia public-owner observed installed-price evidence', as_of: '2026-09-10',
  items: [{
    work_item: 'Replace rooftop HVAC unit', original_description: 'RTU replacement, complete and installed',
    unit: 'each', quantity: '2', unit_price: '12500.00', extended_price: '25000.00', currency: 'USD',
    price_date: '2026-06-15', evidence_status: 'Awarded', project_location: 'Lowndes County, Georgia',
    source_label: 'Approved Georgia public owner', source_reference: 'Facility HVAC replacement',
    caveats: ['Observed all-in installed line price; scope and inclusions may differ from this project.'],
    private_snapshot_id: 'never-render-this', artifact_url: 'https://private.example/source.xlsx',
  }],
  warnings: ['Compare scope, quantity, and inclusions before relying on this observed line.'],
  policy_id: 'never-render-this', source_path: 'private-source-path',
};

/** Safe local-only provenance for the offline source-snapshot visual check. */
const fixtureLocalCostEvidence = [
  {
    source_label: 'Defense Logistics Agency procurement history', evidence_kind: 'historical material observation', contribution: 'used',
    as_of: '2026-09-01', detail: 'A reviewed local procurement-history snapshot supplied an exact mapped material component.',
  },
  {
    source_label: 'GSA Global Supply facility-material catalog', evidence_kind: 'catalog material price', contribution: 'used',
    as_of: '2026-09-02', detail: 'A reviewed local catalog snapshot supplied an exact mapped material component.',
  },
  {
    source_label: 'Georgia Department of Transportation bid tabs', evidence_kind: 'all-in installed civil work', contribution: 'used',
    as_of: '2026-08-15', detail: 'A reviewed local bid-tab observation supplied an all-in installed civil work reference.',
  },
  {
    source_label: 'U.S. Geological Survey mineral and aggregate releases', evidence_kind: 'commodity context', contribution: 'context',
    as_of: '2026-07-01', detail: 'A reviewed local mineral release was used as context only and did not create a price line.',
  },
];

/** A synthetic DLA selection demonstrates replacement, not additive pricing. */
const fixtureDlaMaterialReplacement = {
  material_lines: approximateCostPlanFixture.material_lines.map((line) => line.label === 'Type W screws'
    ? { ...line, label: 'Wood-framing screws', source_label: 'Defense Logistics Agency procurement history', source_date: '2026-08-17', geography_label: 'United States' }
    : line),
  material_component_replacements: [{
    source_label: 'Defense Logistics Agency procurement history', component_label: 'Wood-framing screws',
    release_date: '2026-08-17', package_label: '5 pounds per box',
    private_record_locator: 'fixture-private-dla-record',
  }],
};

/**
 * Synthetic, local-only paint result. Its 350 coverage and 10% waste entries
 * model acknowledged reviewer inputs, never DLA or manufacturer evidence.
 */
const fixturePaintRom: ApproximateCostPlanPublic = {
  scope_label: 'Interior painting',
  status_label: 'Partial, source-backed estimate',
  installed_quantity_label: '1,000 sq ft painted wall surface area',
  purchased_quantity_label: '7 gallons purchased',
  assumptions: [
    'Painted wall surface area is used; floor area is not used.',
    'Finish paint uses 2 coats, 350 square feet per gallon per coat, and 10% waste.',
    'Coverage and waste are human/reviewer-supplied planning assumptions, not DLA price data or manufacturer coverage evidence.',
    'Published roller-application hours are a planning reference; access and conditions can change actual labor.',
  ],
  exclusions: ['Primer, consumables and small tools, surface patching, phasing, hazardous-material work, rentals, freight, tax, delivery, and margin are not estimated.'],
  labor_total: '480.00',
  materials_total: '473.62',
  priced_subtotal: '953.62',
  labor_lines: [{
    label: 'Roller-applied finish paint', craft_label: 'Painter', quantity_label: '1,000 sq ft painted wall surface area',
    hours_per_unit: '0.016', hours: '16', base_wage: '24.00', fringe: '6.00', amount: '480.00',
    source_label: 'Wage Determination GA20260316 revision 2', source_date: '2026-05-18', geography_label: 'Lowndes County, Georgia',
    assumptions: ['Synthetic fixture: published roller-application planning hours and a reviewed Painter mapping; two finish coats.'],
    source_url: 'https://sam.gov/wage-determination/GA20260316/2',
  }],
  labor_coverage: {
    status: 'complete', missing_crafts: [],
    provenance_label: 'Synthetic fixture: Wage Determination GA20260316 wage and fringe with a reviewed Painter mapping.',
  },
  material_lines: [{
    label: 'Interior flat latex paint', specification: 'Reviewed flat interior latex paint, one gallon', sku: 'fixture-interior-flat-latex-1gal',
    consumption_label: '5.714 gallons net; two finish coats and 10% waste', purchased_quantity_label: '7 gallons purchased',
    package_label: '1 gallon per package', unit_price: '67.66', amount: '473.62', source_label: 'Defense Logistics Agency procurement history',
    source_date: '2026-02-04', geography_label: 'Historical federal procurement observation',
    assumptions: ['Synthetic fixture: historical DLA observation; not a current local supplier quote.'],
  }],
  material_component_replacements: [{
    source_label: 'Defense Logistics Agency procurement history', component_label: 'Interior flat latex paint', release_date: '2026-02-04', package_label: '1 gallon per package',
  }],
  cost_evidence: [{
    source_label: 'Defense Logistics Agency procurement history', evidence_kind: 'historical material observation', contribution: 'used', as_of: '2026-02-04',
    detail: 'A reviewed local procurement-history snapshot supplied the paint package price.',
  }],
  other_costs: [
    { label: 'Primer', amount: null, basis: 'Required when applicable; not estimated' },
    { label: 'Surface patching and preparation', amount: null, basis: 'Required when applicable; not estimated' },
    { label: 'Consumables and small tools', amount: null, basis: 'Required; not estimated' },
    { label: 'Phasing, protection, and access conditions', amount: null, basis: 'Project-specific; not estimated' },
    { label: 'Hazardous-material work and rentals', amount: null, basis: 'Project-specific; not estimated' },
    { label: 'Freight, tax, delivery, and margin', amount: null, basis: 'Required; not estimated' },
  ],
  award_reference: { amount: '2000.00', low: '1800.00', high: '2200.00', compatible: true, basis_label: 'Hypothetical same-scope comparison for this fixture' },
  warnings: ['Synthetic local fixture only. DLA material history is not a current supplier quote.', 'Required painting costs remain not estimated.'],
};

const fixturePaintRomWithoutAcknowledgedCoverage: ApproximateCostPlanPublic = {
  ...fixturePaintRom,
  status_label: 'Partial estimate — paint material pending reviewed assumptions',
  purchased_quantity_label: 'Paint packages not estimated',
  assumptions: fixturePaintRom.assumptions.filter((assumption) => !assumption.startsWith('Finish paint uses')),
  labor_total: '480.00', materials_total: null, priced_subtotal: '480.00', material_lines: [], material_component_replacements: [], cost_evidence: [],
  warnings: ['Finish-paint material cost is not estimated until coverage and waste planning assumptions have been provided.', 'Required painting costs remain not estimated.'],
};

/**
 * Synthetic presentation data for the local browser fixture only. The real
 * source importer is covered separately: this response proves no live fetch
 * occurred while exercising the owner-facing primer controls and provenance.
 */
const fixturePrimerRom: ApproximateCostPlanPublic = {
  scope_label: 'Interior primer', status_label: 'Partial, source-backed estimate',
  installed_quantity_label: '1,000 sq ft measured primed surface area',
  purchased_quantity_label: '3 gallons purchased',
  assumptions: [
    'PPG Speedhide 6-2 interior latex quick-dry sealer is confirmed for the selected primer product and prepared interior substrate.',
    'One primer coat at 400 square feet per gallon is PPG technical-data nominal coverage for confirmed smooth, nonporous surfaces (revision 3/2020).',
    'The selected public retailer observation uses three one-gallon packages for 2.5 gallons required; 0% project waste was confirmed.',
  ],
  exclusions: ['Installation labor, surface repair, masking and protection, freight, tax, delivery, other costs, and margin are not estimated.'],
  labor_total: null, materials_total: '74.97', priced_subtotal: '74.97', labor_lines: [],
  labor_coverage: { status: 'unavailable', missing_crafts: [], reason: 'This exact primer-material component does not estimate installation labor.' },
  material_lines: [{
    label: 'PPG Speedhide 6-2 primer', specification: 'PPG Speedhide 6-2 interior latex quick-dry sealer primer, 1-gallon container',
    sku: 'ppg-speedhide-6-2-primer-1-gallon', consumption_label: '2.5 gallons net; 1 primer coat and 0% waste',
    purchased_quantity_label: '3 gallons purchased; 0.5 gallon package remainder', package_label: '1 gallon per package',
    unit_price: '24.99', amount: '74.97', source_label: 'Reviewed public retailer observation',
    source_date: '2026-09-20', geography_label: 'Public retailer observation',
    assumptions: ['Synthetic fixture only: reviewed public retailer observation. Delivery, freight, and tax are not included.'],
    source_url: 'https://shop.columbiapaintco.com/products/ppg-speedhide-6-2-primer',
  }],
  material_component_breakdown: [{ label: 'PPG Speedhide 6-2 interior primer', amount: '74.97', source_labels: ['Reviewed public retailer observation'] }],
  sources_checked: [
    { label: 'Reviewed public retailer observation', detail: 'A reviewed public retailer snapshot supplied the one- and five-gallon package prices used here.' },
    { label: 'PPG technical data', detail: 'Revision 3/2020 supplied the 400-square-feet-per-gallon nominal coverage condition for smooth, nonporous surfaces.' },
  ],
  other_costs: [
    { label: 'Installation labor', amount: null, basis: 'Not estimated by this exact material source.' },
    { label: 'Surface preparation, masking, and protection', amount: null, basis: 'Project-specific; not estimated.' },
    { label: 'Freight, tax, delivery, and margin', amount: null, basis: 'Not included in the observed retailer price.' },
  ],
  award_reference: null,
};

const fixtureDuplexRom: ApproximateCostPlanPublic = {
  ...approximateCostPlanFixture,
  scope_label: 'Standard duplex receptacle', installed_quantity_label: '20 each confirmed material quantity',
  purchased_quantity_label: '20 each purchased', labor_total: null, labor_lines: [], materials_total: '251.60',
  priced_subtotal: '251.60', other_costs: [],
  assumptions: ['Confirmed 20A, 125V AC, 2-pole/3-wire grounding NEMA 5-20R standard duplex receptacle.'],
  exclusions: ['Circuits, boxes, wire, cover plates, labor, freight, tax, and installed electrical work are not estimated.'],
  material_lines: [{
    label: 'Standard duplex receptacle', amount: '251.60', source_label: 'GSA Multiple Award Schedule catalog', source_date: '2023-12-18',
    geography_label: 'Nationwide government catalog observation', assumptions: ['Historical 2023 catalog material evidence; freight and tax are not included.'],
    specification: 'Hubbell HBL5352I standard duplex receptacle, 20A 125V 2P 3W grounding NEMA 5-20R',
    sku: 'standard-duplex-receptacle', consumption_label: '20 each net consumption', purchased_quantity_label: '20 each purchased',
    package_label: '1 each per package', unit_price: '12.58',
    source_url: 'https://www.gsaadvantage.gov/ref_text/GS07F0196U/0YYLRW.3UOYMK_GS-07F-0196U_WESCOGSAPRICELIST231221.PDF',
  }],
  material_component_breakdown: [{ label: 'Standard duplex receptacle', amount: '251.60', source_labels: ['GSA Multiple Award Schedule catalog'] }],
  labor_coverage: { status: 'unavailable', missing_crafts: [], reason: 'This exact catalog-material pilot does not estimate installation labor.' },
};

/** Synthetic public BTB display data only; private-looking fields prove the client allowlist does not render them. */
const fixtureBtbPlanningBaseline = {
  source_label: 'DOE/NREL Buildings Technology Baseline (2024)', evidence_type: 'National planning baseline',
  technology_label: 'Commercial air-source heat-pump equipment', building_segment: 'commercial', cost_basis_label: 'Installed',
  value: { amount: '4800.25', currency: 'USD', unit_label: 'per installed unit' }, value_year_label: '2024 dollars',
  scenario_label: 'Standard efficiency scenario', geography_label: 'National',
  attribution: 'Source: DOE/NREL Buildings Technology Baseline (2024), CC BY 4.0.',
  limitations: ['Planning baseline for a supported building-energy technology; not a project-specific quote or complete construction estimate.'],
  policy_id: 'private-btb-policy', artifact_hash: 'private-btb-artifact', manifest_id: 'private-btb-manifest',
  raw_source_row: 'private-source-row', source_url: 'https://example.invalid/private',
};

/**
 * A local-only, server-shaped mixed takeoff projection. It intentionally
 * contains cited display facts and lifecycle IDs but no source query, prompt,
 * raw SOW, catalog key, or pricing selection. The fixture proves that the
 * canonical `ready_to_review` status reaches the existing owner review UI.
 */
const fixtureMixedComponentTakeoffProposals = [
  {
    proposal_id: 'drywall-room-101', occurrence_id: 'room101:drywall-1', component_kind: 'drywall_level4_wood', label: 'Drywall repair — Room 101',
    status: 'ready_to_review', quantity_provenance: 'directly_stated', source_label: 'SOW', source_locator: 'page 2',
    excerpt: 'Repair 500 square feet of damaged drywall in Room 101.', excerpt_sha256: `sha256:${'a'.repeat(64)}`,
    quantity: '500', unit: 'square_foot', confidence: 'high', missing_facts: [],
  },
  {
    proposal_id: 'paint-room-101', occurrence_id: 'room101:paint-1', component_kind: 'interior_paint', label: 'Interior wall finish — Room 101',
    status: 'ready_to_review', quantity_provenance: 'ai_planning_estimate', source_label: 'Salient characteristics', source_locator: 'page 2',
    excerpt: 'Restore finish paint after the wall repair.', excerpt_sha256: `sha256:${'b'.repeat(64)}`,
    quantity: '900', unit: 'square_foot', confidence: 'high', planning_range: { low: '800', base: '900', high: '1000' },
    rationale: 'The SOW names wall repair but does not state the paintable wall area.', assumptions: ['Assumes the repaired wall faces require one finish area.'], missing_facts: [],
  },
  {
    proposal_id: 'primer-room-101', occurrence_id: 'room101:primer-1', component_kind: 'interior_primer', label: 'Interior primer — North wall',
    status: 'ready_to_review', quantity_provenance: 'derived_from_cited_facts', source_label: 'SOW', source_locator: 'page 3',
    excerpt: 'Prime the repaired north wall before finish paint.', excerpt_sha256: `sha256:${'c'.repeat(64)}`,
    quantity: '500', unit: 'square_foot', formula: '500 square feet of stated repair area', formula_evidence: ['SOW page 2: 500 square feet of drywall repair.'],
    confidence: 'medium', missing_facts: [],
  },
];

/** Server-owned status disclosure: no browser residual/category inference. */
const fixtureMixedComponentCompleteness = {
  basis: 'extracted_scope' as const,
  counts: { priced: 1, unpriced: 1, excluded: 1 },
  items: [
    { label: 'Drywall repair — Room 101', status: 'priced' as const, reason: 'A compatible reviewed component price is included in Materials.', provenance_label: 'From SOW', source_label: 'USAspending award records', source_locator: 'award record' },
    { label: 'Interior primer — North wall', status: 'unpriced' as const, reason: 'No compatible public itemized price is included yet.', provenance_label: 'Calculated from SOW', source_label: 'Statement of work', source_locator: 'page 3' },
    { label: 'Obsolete wallpaper removal', status: 'excluded' as const, reason: 'Owner rejected this out-of-scope occurrence.', provenance_label: 'Owner decision', source_label: 'Statement of work', source_locator: 'page 4' },
  ],
};

const fixtureLoad = () => {
  const scenario = scenarioName();
  const viewer = scenario === 'viewer';
  const noCoverage = scenario === 'no-coverage';
  const btbEligible = scenario === 'btb-eligible';
  const unified = scenario === 'unified';
  const georgiaObserved = scenario === 'ga-observed';
  const paintRom = scenario === 'rom-paint' || scenario === 'rom-paint-no-ack';
  const primerRom = scenario === 'rom-primer';
  const paintRomAcknowledged = scenario === 'rom-paint';
  const componentQueue = scenario === 'rom-component-queue';
  const mixedComponentTakeoff = scenario === 'rom-component-takeoff-mixed';
  const componentSelected = savedRomSettings().material_components.some((item) => item.component_key === '20a_125v_5_20r_standard_duplex_receptacle');
  const rom = scenario.startsWith('rom');
  const running = savedJob() === 'running';
  const completed = savedJob() === 'completed';
  const visibleRevision = unified || georgiaObserved || rom
    ? {
      ...fixtureRevision,
      ...(scenario === 'rom-labor-plan' ? {
        revision: fixtureRevision.revision
          + Number(window.localStorage.getItem(calculationCountKey) ?? 0),
      } : {}),
      ...(rom ? { approximate_cost_plan: { ...approximateCostPlanFixture,
        ...(paintRom ? (paintRomAcknowledged ? fixturePaintRom : fixturePaintRomWithoutAcknowledgedCoverage) : {}),
        ...(primerRom ? fixturePrimerRom : {}),
        ...(scenario === 'rom-hvac' ? { scope_label: 'HVAC replacement — no matched cost recipe', status_label: 'No source-backed recipe available yet', labor_lines: [], material_lines: [], labor_total: null, materials_total: null, priced_subtotal: null, unallocated_difference: null, comparison_label: 'Different scope/basis — not subtracted', award_reference: { ...approximateCostPlanFixture.award_reference, compatible: false } } : {}),
        ...(scenario === 'rom-labor-unavailable' ? {
          labor_total: null,
          labor_lines: [],
          labor_coverage: {
            status: 'unavailable',
            reason: 'Wage Determination rates are not available for this fixture.',
            missing_crafts: ['Carpenter'],
            provenance_label: 'Synthetic fixture: Wage Determination GA20260316 was identified for Lowndes County, Georgia.',
          },
          priced_subtotal: '695.89',
          unallocated_difference: '1304.11',
        } : {}),
        ...(scenario === 'rom-fee' ? { fee: { amount: '200.00', rate_label: '10%', basis_label: 'Assumed share of award reference' }, priced_subtotal: '1474.80', unallocated_difference: '525.20' } : {}),
        ...(scenario === 'rom-overrun' ? { unallocated_difference: '-100.00' } : {}),
        ...(scenario === 'rom-incompatible' ? { award_reference: { ...approximateCostPlanFixture.award_reference, compatible: false }, unallocated_difference: null, comparison_label: 'Different scope/basis — not subtracted' } : {}),
        ...(scenario === 'rom-local-sources' ? { ...fixtureDlaMaterialReplacement, cost_evidence: fixtureLocalCostEvidence } : {}),
        ...(componentQueue && componentSelected ? fixtureDuplexRom : {}),
        ...(mixedComponentTakeoff ? { component_completeness: fixtureMixedComponentCompleteness } : {}),
      } } : {}),
      unified_source_research: {
        ...fixtureUnifiedResearch,
        ...(georgiaObserved ? {
          georgia_observed_evidence: fixtureGeorgiaObservedEvidence,
          recommendation: { ...fixtureUnifiedResearch.recommendation, applicability_label: 'Lowndes County, Georgia' },
        } : {}),
      },
      comparables: { items: [{ award_id: 'FA-LOCAL-1', description: 'Comparable repair', original_amount: '250000', action_date: '2025-05-12', included: true, place_of_performance_state: georgiaObserved ? 'GA' : 'TX', place_of_performance_city: georgiaObserved ? 'Valdosta' : 'Houston', place_of_performance_county: georgiaObserved ? 'Lowndes' : 'Harris' }] },
    }
    : btbEligible
    ? { ...fixtureRevision, btb_planning_baseline: fixtureBtbPlanningBaseline }
    : fixtureRevision;
  return {
    capabilities: {
      applicable: true, enabled: true, calculation_available: true, export_available: false,
      can_edit: !viewer, can_calculate: !viewer, can_finalize: !viewer, path_policy_version: scenario === 'grandfathered' ? 1 : 2,
      approximate_cost_plan: {
        supported_recipes: [
          { recipe_key: 'drywall_level4_wood_v1', label: 'Drywall', scope: 'Drywall', allowed_units: ['square_foot'] },
          { recipe_key: 'interior_paint_flat_latex_v1', label: 'Interior painting', scope: 'Interior painting', allowed_units: ['square_foot'] },
        ],
      },
      cost_data_primary: noCoverage ? { status: 'no_eligible_coverage' } : { status: 'eligible' },
    },
    draft: rom ? { ...draft, approximate_cost_plan_settings: componentQueue ? withAppliedLaborPlan(componentSelected ? savedRomSettings() : emptyApproximateSettings()) : mixedComponentTakeoff ? withAppliedLaborPlan(savedMixedTakeoffSettings()) : paintRom ? paintRomSettings(paintRomAcknowledged) : primerRom ? withAppliedLaborPlan(savedPrimerRomSettings()) : scenario === 'rom-labor-plan' ? (window.localStorage.getItem(romSettingsKey) ? savedRomSettings() : laborPlanRomSettings()) : scenario === 'rom-roof-work' ? withAppliedLaborPlan(savedRomSettings()) : scenario === 'rom-hvac' ? { ...emptyApproximateSettings(), work_item_description: 'Replace rooftop HVAC equipment', net_quantity: '2', quantity_unit: 'each', setup_proposal: { status: 'no_match', candidate_description: 'Replace rooftop HVAC equipment', quantity: '2', unit: 'each', source_stage: 604, evidence_state: 'directly_stated' } } : scenario === 'rom-proposal' ? { ...savedRomSettings(), enabled: false, work_item_description: 'Drywall', setup_proposal: { status: 'matched', candidate_description: 'Drywall', quantity: '1000', unit: 'square_foot', source_stage: 604, evidence_state: 'directly_stated' } } : savedRomSettings() } : georgiaObserved ? { ...draft, project_location: { country: 'US', state: 'GA', county: 'Lowndes', city: 'Valdosta', postal_code: '31601' } } : draft,
    active_revision: noCoverage ? null : visibleRevision,
    revision_history: [],
    job: running ? { job_id: 'fixture-cost-job', status: 'running', capabilities: { can_cancel: true } } : null,
    readiness: { ready_to_calculate: true, blockers: [] },
    cost_data_summary: noCoverage ? {
      decision: 'insufficient_item_evidence', status: 'no_eligible_coverage', draft_revision: draft.draft_revision,
      covered_item_count: 0, included_item_count: 1, unresolved_items: [{ label: 'Hot-mix asphalt pavement', next_action: 'add_allowance_or_exclude' }],
      source_freshness: 'not_available', provenance: [],
    } : null,
    ...(completed ? { active_revision: visibleRevision } : {}),
    ...(componentQueue ? { component_confirmation_queue: [{
      candidate_id: 'requirement:outlets-20', label: 'Electrical outlets', status: 'Specification confirmation needed',
      excerpt: 'Provide and install at least 20 outlets in the OPS center.', source_label: 'SOW', source_locator: 'page 3', quantity: '20', unit: 'each',
      missing_fields: ['Outlet device, color, and grade must be confirmed.'], approved_option: {
        component_key: '20a_125v_5_20r_standard_duplex_receptacle', label: 'Standard duplex receptacle', specification: '20A, 125V, 2-pole/3-wire, grounding, NEMA 5-20R',
        source_label: 'GSA Multiple Award Schedule catalog', source_date: '2023-12-18',
        source_url: 'https://www.gsaadvantage.gov/ref_text/GS07F0196U/0YYLRW.3UOYMK_GS-07F-0196U_WESCOGSAPRICELIST231221.PDF',
        disclosure: 'Historical 2023 catalog material evidence. It excludes circuits, boxes, wire, cover plates, labor, freight, tax, and installed electrical work.',
      },
    }] } : {}),
    ...(mixedComponentTakeoff ? {
      prefill: {
        prefill_revision: 9, status: 'current', outcome: 'prepared',
        provenance: { source: 'Synthetic local fixture' },
        component_takeoff_proposals: fixtureMixedComponentTakeoffProposals,
      },
    } : {}),
    ...(primerRom ? { active_catalog_material_components: ['ppg_speedhide_6_2_interior_primer'] } : {}),
    ...(scenario === 'rom-labor-plan' ? { labor_plan_proposal: window.localStorage.getItem(laborPlanStatusKey)
      ? { ...fixtureLaborProposal, status: window.localStorage.getItem(laborPlanStatusKey) }
      : null } : {}),
  };
};

/** Browser-only isolated fixture. It permits just the stage's local load/save/job routes. */
function installFixtureFetch() {
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = typeof input === 'string' ? input : input.toString();
    const url = new URL(request, window.location.origin);
    const method = (init?.method ?? 'GET').toUpperCase();
    const base = `/api/construction-price-estimator/${flowId}`;
    const body = () => typeof init?.body === 'string' ? JSON.parse(init.body) as Record<string, unknown> : {};
    if (url.pathname === base && method === 'GET') return response(fixtureLoad());
    if (url.pathname === `${base}/labor-plan-preview` && method === 'GET') return response(fixtureLaborPreview);
    if (scenarioName() === 'rom-labor-plan' && url.pathname === `${base}/labor-plan-jobs` && method === 'POST') {
      laborFixtureJobPolls = 0;
      window.localStorage.removeItem(laborPlanStatusKey);
      return response({ job_id: 'fixture-labor-job', status: 'running' }, 202);
    }
    if (scenarioName() === 'rom-labor-plan' && url.pathname === `${base}/labor-plan-jobs/fixture-labor-job` && method === 'GET') {
      laborFixtureJobPolls += 1;
      if (laborFixtureJobPolls < 16) return response({ job_id: 'fixture-labor-job', status: 'running' });
      window.localStorage.setItem(laborPlanStatusKey, 'proposed');
      return response({ job_id: 'fixture-labor-job', status: 'completed', proposal: fixtureLaborProposal });
    }
    if (scenarioName() === 'rom-labor-plan' && url.pathname === `${base}/labor-plan-proposals/${fixtureLaborProposal.proposal_id}/apply` && method === 'POST') {
      const submitted = body();
      if (window.localStorage.getItem(laborPlanStatusKey) !== 'proposed'
        || submitted.expected_draft_revision !== draft.draft_revision
        || submitted.expected_draft_content_hash !== draft.content_hash
        || !Array.isArray(submitted.site_staffing_roles)) {
        return new Response(JSON.stringify({ success: false, error: 'The labor plan must be reviewed before applying.' }), { status: 409, headers: { 'Content-Type': 'application/json' } });
      }
      const settings = {
        ...savedRomSettings(),
        site_staffing_roles: submitted.site_staffing_roles,
        removed_site_staffing_role_kinds: ['project_manager', 'quality_control'],
        labor_plan_provenance: fixtureLaborProposal.proposal_id,
      };
      window.localStorage.setItem(romSettingsKey, JSON.stringify(settings));
      draft = { ...draft, draft_revision: draft.draft_revision + 1, content_hash: `fixture-accepted-${draft.draft_revision + 1}` };
      window.localStorage.setItem(laborPlanStatusKey, 'applied');
      return response({ draft: { ...draft, approximate_cost_plan_settings: settings }, proposal: { ...fixtureLaborProposal, status: 'applied' } });
    }
    if (url.pathname === `${base}/draft` && method === 'PUT') {
      const saved = body();
      if (scenarioName().startsWith('rom') && saved.approximate_cost_plan_settings) window.localStorage.setItem(romSettingsKey, JSON.stringify(saved.approximate_cost_plan_settings));
      draft = { ...draft, ...saved, draft_revision: draft.draft_revision + 1, content_hash: `fixture-accepted-${draft.draft_revision + 1}` };
      return response(draft);
    }
    if ((url.pathname === `${base}/calculations` || url.pathname === `${base}/research`) && method === 'POST') {
      const requestBody = body();
      const allowed = ['expected_draft_revision', 'expected_draft_content_hash', 'force_refresh', 'ai_planning_estimates_acknowledged'];
      if (Object.keys(requestBody).some((key) => !allowed.includes(key))) return new Response(JSON.stringify({ success: false, error: 'Unexpected fixture request.' }), { status: 400 });
      window.localStorage.setItem('construction-cost-snapshot-e2e-last-calculation-body', JSON.stringify(requestBody));
      storeJob('running');
      return response({ job_id: 'fixture-cost-job', status: 'running', capabilities: { can_cancel: true } }, 202);
    }
    if (url.pathname === `${base}/calculations/fixture-cost-job` && method === 'GET') {
      if (savedJob() === 'running' && scenarioName() !== 'running'
        && window.localStorage.getItem('construction-cost-snapshot-e2e-hold-job') !== '1') {
        storeJob('completed');
        if (scenarioName() === 'rom-labor-plan') {
          const count = Number(window.localStorage.getItem(calculationCountKey) ?? 0);
          window.localStorage.setItem(calculationCountKey, String(count + 1));
        }
        return response({ job_id: 'fixture-cost-job', status: 'completed' });
      }
      return response({ job_id: 'fixture-cost-job', status: savedJob() ?? 'completed', capabilities: { can_cancel: savedJob() === 'running' } });
    }
    if (url.pathname === `${base}/calculations/fixture-cost-job/cancel` && method === 'POST') {
      storeJob('cancelled');
      return response({ job_id: 'fixture-cost-job', status: 'cancelled' });
    }
    return new Response(JSON.stringify({ success: false, error: 'Unexpected fixture request.' }), { status: 404, headers: { 'Content-Type': 'application/json' } });
  };
}

export default function ConstructionCostSnapshotFixture() {
  if (shouldInstallConstructionCostSnapshotFetchShim()) installFixtureFetch();
  const viewer = scenarioName() === 'viewer';
  return (
    <ThemeProvider theme={createAppTheme()}>
      <CssBaseline />
      <FlowContext.Provider value={{ flowName: 'Construction cost snapshot fixture', canGoBack: false, standaloneMode: true, isViewOnly: viewer }}>
        <Box component="main" data-testid="construction-cost-snapshot-e2e-shell" sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 3 }}>
          <Typography variant="overline" color="primary">Browser test fixture</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Synthetic local data only. No user profile, workflow, source system, or production service is used.</Typography>
          <ConstructionPriceEstimator flowId={flowId} pollIntervalMs={250} />
        </Box>
      </FlowContext.Provider>
    </ThemeProvider>
  );
}
